import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button } from '@blade/ui-kit'
import {
  WalletAcknowledgments,
  type AcknowledgmentValue,
} from '@root/entities/wallet/ui'
import { useBreakpoint } from '@root/shared'
import { FadeOverflow, mediaMinTablet } from '@root/shared/ui-kit'

import { CreateWalletGraphic } from './create-wallet-graphic'

import type { CreateWalletFlowModel } from '../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: CreateWalletFlowModel
} & ClassNameProps

export const CreateWalletAcknowledgements: React.FC<Props> = ({
  model,
  className,
}) => {
  const { t } = useTranslation()

  const [state] = useAtom(model.deviceWalletStateAtom)
  const [value, setValue] = useState<AcknowledgmentValue>({
    first: false,
    second: false,
    third: false,
  })
  const enabled = value.first && value.second && value.third

  const generate = useAction(model.generateAction)
  const breakpoint = useBreakpoint()

  return (
    <Root className={className}>
      <Body variant="vertical" mode="always" blockEnd={16} blockStart={32}>
        <Content>
          <div>
            <Graphic />
          </div>
          <ContentInner>
            <Title>{t('wallet.create.seed-phrase-title')}</Title>
            <Description>
              {t('wallet.create.seed-phrase-check-boxes')}
            </Description>
            <WalletAcknowledgments value={value} onChange={setValue} />
          </ContentInner>
        </Content>
      </Body>
      <Footer>
        <Button
          fullWidth
          disabled={!enabled}
          onClick={() => generate()}
          variant="solid"
          color="primary"
          size={breakpoint === 'mobile' ? 'h40' : 'h36'}
          radius="rounded"
          busy={state.loading}
        >
          {t('common.next')}
        </Button>
      </Footer>
    </Root>
  )
}

const Root = styled.div`
  overflow: auto;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
`

const Body = styled(FadeOverflow)`
  overflow: auto;
  padding-inline: 12px;
`

const Content = styled.div`
  display: grid;
  position: relative;

  overflow: auto;
  grid-template-rows: auto 1fr;
`

const ContentInner = styled.div`
  position: relative;
  top: -40px;
  display: grid;
  align-content: start;
`

const Graphic = styled(CreateWalletGraphic)`
  max-width: 100%;
  max-height: 264px;
  margin-inline: auto;
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;

  font-size: 24px;
  font-weight: 700;
  line-height: 120%;
  margin-bottom: 16px;

  ${mediaMinTablet(css`
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    margin-bottom: 14px;
  `)}
`

const Description = styled.div`
  color: var(--text-10);

  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 32px;

  ${mediaMinTablet(css`
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin-bottom: 20px;
  `)}
`

const Footer = styled.div`
  padding-inline: 12px;
  padding-top: 4px;
`
